import { DateTime } from 'luxon'

import { PagedResult } from '../DTOs/Common/PagedResult'
import type { DiggingLoadingSummaryDto } from '../DTOs/Production/DiggingLoadingSummaryDto'
import { DiggingLocationDTO } from '../DTOs/Production/DiggingLocationDTO'
import { StoppageCalc } from '../DTOs/Stoppage/StoppageCalc'
import { queryClient } from '../queryClient'
import { sortCachedRows } from './filterCachedRows'

type SummaryResult = PagedResult<DiggingLoadingSummaryDto> & { totalLoads?: number; totalTonnes?: number }

function summaryKey(shiftDate: DateTime, shift: string): unknown[] {
  return ['dataTable', 'chatree', 'diggingLoadingSummary', shiftDate.toISODate(), shift, 'all', false, null]
}

function machineName(plantId: number): string {
  for (const [, page] of queryClient.getQueriesData<PagedResult<StoppageCalc>>({
    queryKey: ['dataTable', 'operations', 'stoppageCalcs'],
  })) {
    const match = (page?.data ?? []).find(row => row?.plantId === plantId)
    if (match?.machineName) return match.machineName
  }
  return ''
}

export function appendCachedDiggingSummaryRow(
  location: DiggingLocationDTO,
  shiftDate: DateTime,
  shift: string,
  diggerId: number,
): void {
  const key = summaryKey(shiftDate, shift)
  const cached = queryClient.getQueryData<SummaryResult>(key)
  const rows = cached?.data ?? []

  if (rows.some(row => row.diggingPatternId === location.diggingPatternId)) return

  const next: DiggingLoadingSummaryDto[] = [
    ...rows,
    {
      diggingId: diggerId,
      diggingPatternId: location.diggingPatternId,
      plantId: diggerId,
      excavator: machineName(diggerId),
      pitStage: location.pitStage,
      blastMasterId: String(location.blastMasterId ?? ''),
      startTime: location.startTime,
      loads: 0,
      tonnes: 0,
    },
  ]

  writeSummary(key, cached, next)
}

function writeSummary(
  key: unknown[],
  cached: SummaryResult | undefined,
  rows: DiggingLoadingSummaryDto[],
  totals?: { loads: number; tonnes: number },
): void {
  const ordered = sortCachedRows(rows, {
    accessors: {},
    fallback: row => row.excavator,
  })

  queryClient.setQueryData(key, {
    ...(cached ?? new PagedResult<DiggingLoadingSummaryDto>([], 0, 1, 1, 1)),
    data: ordered,
    totalCount: ordered.length,
    pageNumber: 1,
    pageSize: Math.max(ordered.length, 1),
    totalLoads: Math.max(0, (cached?.totalLoads ?? 0) + (totals?.loads ?? 0)),
    totalTonnes: Math.max(0, (cached?.totalTonnes ?? 0) + (totals?.tonnes ?? 0)),
  })
}

export function addQueuedLoadToCachedDiggingSummary(
  load: { diggingPatternId: number; loads: number; tonnes?: number },
  shiftDate: DateTime,
  shift: string,
  sign: 1 | -1 = 1,
): void {
  const key = summaryKey(shiftDate, shift)
  const cached = queryClient.getQueryData<SummaryResult>(key)
  const rows = cached?.data
  if (!rows?.some(row => row.diggingPatternId === load.diggingPatternId)) return

  writeSummary(key, cached, rows.map(row => row.diggingPatternId === load.diggingPatternId
    ? {
      ...row,
      loads: Math.max(0, (row.loads ?? 0) + sign * (load.loads ?? 0)),
      tonnes: Math.max(0, (row.tonnes ?? 0) + sign * (load.tonnes ?? 0)),
    }
    : row), { loads: sign * (load.loads ?? 0), tonnes: sign * (load.tonnes ?? 0) })
}

export function sumCachedLoads(listKey: unknown[]): number {
  const cached = queryClient.getQueryData<{ data?: { loads?: number }[] }>(listKey)
  return (cached?.data ?? []).reduce((total, row) => total + (row?.loads ?? 0), 0)
}
